import type { QueryClient } from "@tanstack/react-query";
import type { WorkItem, Workflow } from "../workflows/types";
import type { WorkItemListResponse } from "../workitems/types";
import type {
    WorkItemCompletedNotification,
    WorkItemEditedNotification,
    WorkItemLockedNotification,
    WorkItemSnoozedNotification,
    WorkItemUnlockedNotification,
} from "./notifications";

// ---------------------------------------------------------------------------
// Optimistic cache patches for realtime completion events.
//
// The Roundtable's verdict for slice 6 (`docs/REALTIME.md`):
//
//   * `WorkItemCompleted` carries every field that actually changes on a
//     completion (`completedAt`, `completedBy`, `completedByDisplayName`)
//     plus the identifiers needed to locate the cached row. The cache
//     can be patched in-place; no refetch is required to render the new
//     state. This eliminates a ~150ms perceived-latency stutter where a
//     just-completed item lingers without its checkmark.
//
//   * `WorkItemAdded` carries only identifiers (`workItemId`, `type`,
//     `visibleToQueueIds`, `awaitingActionByQueueIds`). It is *not*
//     enough to render a new `WorkItem` row — the cache patch would
//     have to invent `body`, `createdBy`, etc. The existing
//     invalidation + refetch path stays in place for adds, deliberately.
//     The two queue lists arrive on the wire so the tally hook can
//     decide whether to invalidate `["queues","tallies",…]` (driven by
//     `awaitingActionByQueueIds`) without a follow-up fetch — only the
//     awaiting set changes a queue badge.
//
// The two patch functions below are intentionally feature-aware: they
// know the `WorkItem` and `Workflow` shapes from the workflows feature
// because the patch *is* a shape-aware operation. The architectural
// alternative — emitting per-feature patch hooks from each feature —
// would scatter realtime knowledge across the codebase; centralizing
// it here keeps the realtime layer's responsibilities narrow and
// auditable.
// ---------------------------------------------------------------------------

const QUEUE_LIST_KEY_PREFIX = ["workitems", "byQueue"] as const;
const MINE_LIST_KEY_PREFIX = ["workitems", "mine"] as const;
const WORKFLOW_DETAIL_KEY_PREFIX = ["workflows", "detail"] as const;

interface CompletionPatch {
    readonly completedAt: string;
    readonly completedBy: string | null;
    readonly completedByDisplayName: string | null;
}

function completionPatchFrom(
    notification: WorkItemCompletedNotification,
): CompletionPatch {
    return {
        completedAt: notification.completedAt,
        completedBy: notification.completedBy ?? null,
        completedByDisplayName: notification.completedByDisplayName ?? null,
    };
}

function applyCompletionToItem(item: WorkItem, patch: CompletionPatch): WorkItem {
    // The realtime event is advisory; do not overwrite a cache entry
    // that already records a completion at a later wall-clock time
    // (e.g. a refetch raced and won). While the hub is up, no timer
    // corrects a wrong entry: lists refetch on window focus when stale,
    // the workflow detail does not. After a hub gap, the lifecycle
    // resync (`realtimeLifecycle.ts`) refetches both.
    if (item.completedAt !== null && item.completedAt >= patch.completedAt) {
        return item;
    }
    return {
        ...item,
        completedAt: patch.completedAt,
        completedBy: patch.completedBy,
        completedByDisplayName: patch.completedByDisplayName,
    };
}

/**
 * Patches every cached `WorkItemListResponse` (for `["workitems", "byQueue", …]`
 * and `["workitems", "mine", …]`) so the completed item either disappears
 * (in `includeCompleted=false` lists) or carries its completion fields
 * (in `includeCompleted=true` lists). The `totalCount` decrements only
 * when an entry was actually removed — the per-page list contents and
 * the server-side total stay coherent for the next paginated fetch.
 */
export function patchWorkItemCompletedInQueueLists(
    queryClient: QueryClient,
    notification: WorkItemCompletedNotification,
): void {
    const patch = completionPatchFrom(notification);
    const updater = (
        old: WorkItemListResponse | undefined,
    ): WorkItemListResponse | undefined => {
        if (old === undefined) return undefined;

        const index = old.items.findIndex((i) => i.id === notification.workItemId);
        if (index === -1) {
            // The completed item isn't on this page. Nothing to render-
            // patch. We deliberately leave `totalCount` alone here: the
            // current page is unchanged, and the next paginated fetch
            // will see the authoritative total.
            return old;
        }

        if (old.includeCompleted) {
            // History view — the item stays, with its completion fields
            // populated.
            const items = old.items.map((item, i) =>
                i === index ? applyCompletionToItem(item, patch) : item,
            );
            return { ...old, items };
        }

        // Open-only view — the completed item leaves the list and the
        // count adjusts for the row we just removed.
        const items = old.items.filter((_, i) => i !== index);
        return {
            ...old,
            items,
            totalCount: Math.max(0, old.totalCount - 1),
        };
    };

    queryClient.setQueriesData(
        { queryKey: QUEUE_LIST_KEY_PREFIX },
        updater,
    );
    queryClient.setQueriesData(
        { queryKey: MINE_LIST_KEY_PREFIX },
        updater,
    );
}

/**
 * Patches every cached workflow detail (`["workflows", "detail", workflowId, …]`)
 * by updating the completed item in place. The completed item stays in
 * the timeline — workflow detail is a chronological narrative, not a
 * filter — so we never remove rows.
 */
export function patchWorkItemCompletedInWorkflowDetail(
    queryClient: QueryClient,
    notification: WorkItemCompletedNotification,
): void {
    const patch = completionPatchFrom(notification);
    queryClient.setQueriesData<Workflow>(
        {
            queryKey: [
                ...WORKFLOW_DETAIL_KEY_PREFIX,
                notification.workflowId,
            ] as const,
        },
        (old) => {
            if (old === undefined) return undefined;
            const index = old.workItems.findIndex(
                (i) => i.id === notification.workItemId,
            );
            if (index === -1) return old;
            const workItems = old.workItems.map((item, i) =>
                i === index ? applyCompletionToItem(item, patch) : item,
            );
            return { ...old, workItems };
        },
    );
}

function applySnoozeToItem(
    item: WorkItem,
    notification: WorkItemSnoozedNotification,
): WorkItem {
    // Stale-publish guard: ignore notifications whose `asOf` predates a
    // newer due date already in cache. This handles the rare race where
    // a slow snooze publish overtakes a faster one — the cache should
    // converge on whichever snooze landed last server-side. We compare
    // the new value against the current to detect "is this notification
    // older than what we already know?"; if our cached due date is
    // already later than what this notification proposes, trust the
    // cache (a subsequent refetch will reconcile either way).
    if (item.dueDate !== null && item.dueDate > notification.newDueDate) {
        return item;
    }
    return {
        ...item,
        dueDate: notification.newDueDate,
    };
}

/**
 * Patches every cached workflow detail by replacing the snoozed
 * item's `dueDate` in place. Same shape as the completion patch — the
 * notification carries the only field a snooze changes, so a refetch
 * is unnecessary and the row updates without a flash.
 */
export function patchWorkItemSnoozedInWorkflowDetail(
    queryClient: QueryClient,
    notification: WorkItemSnoozedNotification,
): void {
    queryClient.setQueriesData<Workflow>(
        {
            queryKey: [
                ...WORKFLOW_DETAIL_KEY_PREFIX,
                notification.workflowId,
            ] as const,
        },
        (old) => {
            if (old === undefined) return undefined;
            const index = old.workItems.findIndex(
                (i) => i.id === notification.workItemId,
            );
            if (index === -1) return old;
            const workItems = old.workItems.map((item, i) =>
                i === index ? applySnoozeToItem(item, notification) : item,
            );
            return { ...old, workItems };
        },
    );
}

/**
 * Patches every cached queue / "mine" work item list page so the
 * snoozed row's `dueDate` updates in place. Unlike the completed
 * patch, a snooze never removes a row — an open task with a future
 * due date still belongs in open-only lists, just under the
 * Pending bucket instead of Open.
 */
export function patchWorkItemSnoozedInQueueLists(
    queryClient: QueryClient,
    notification: WorkItemSnoozedNotification,
): void {
    const updater = (
        old: WorkItemListResponse | undefined,
    ): WorkItemListResponse | undefined => {
        if (old === undefined) return undefined;
        const index = old.items.findIndex((i) => i.id === notification.workItemId);
        if (index === -1) return old;
        const items = old.items.map((item, i) =>
            i === index ? applySnoozeToItem(item, notification) : item,
        );
        return { ...old, items };
    };

    queryClient.setQueriesData({ queryKey: QUEUE_LIST_KEY_PREFIX }, updater);
    queryClient.setQueriesData({ queryKey: MINE_LIST_KEY_PREFIX }, updater);
}

// ---------------------------------------------------------------------------
// Lock / unlock patches (docs/NEXT.md — Phase 1). A lock event carries the
// full post-state (holder + expiry), so — like completion and snooze — the
// row is patched in place rather than refetched. Both events never add or
// remove rows: a locked item is still the same item, just annotated, so
// `totalCount` and membership are untouched.
// ---------------------------------------------------------------------------

interface LockPatch {
    readonly lockedBy: string;
    readonly lockedByDisplayName: string | null;
    readonly lockedUntil: string;
}

function lockPatchFrom(notification: WorkItemLockedNotification): LockPatch {
    return {
        lockedBy: notification.lockedBy,
        lockedByDisplayName: notification.lockedByDisplayName ?? null,
        lockedUntil: notification.lockedUntil,
    };
}

function applyLockToItem(item: WorkItem, patch: LockPatch): WorkItem {
    // Stale-publish guard: a later lock (further-out expiry) already in
    // cache wins over an older event that raced behind it. The refetch
    // loop reconciles either way.
    if (item.lockedUntil !== null && item.lockedUntil > patch.lockedUntil) {
        return item;
    }
    return {
        ...item,
        lockedBy: patch.lockedBy,
        lockedByDisplayName: patch.lockedByDisplayName,
        lockedUntil: patch.lockedUntil,
    };
}

function applyUnlockToItem(item: WorkItem): WorkItem {
    if (item.lockedBy === null && item.lockedUntil === null) {
        return item;
    }
    return {
        ...item,
        lockedBy: null,
        lockedByDisplayName: null,
        lockedUntil: null,
    };
}

function patchItemInWorkflowDetail(
    queryClient: QueryClient,
    workflowId: string,
    workItemId: string,
    transform: (item: WorkItem) => WorkItem,
): void {
    queryClient.setQueriesData<Workflow>(
        { queryKey: [...WORKFLOW_DETAIL_KEY_PREFIX, workflowId] as const },
        (old) => {
            if (old === undefined) return undefined;
            const index = old.workItems.findIndex((i) => i.id === workItemId);
            if (index === -1) return old;
            const workItems = old.workItems.map((item, i) =>
                i === index ? transform(item) : item,
            );
            return { ...old, workItems };
        },
    );
}

function patchItemInQueueLists(
    queryClient: QueryClient,
    workItemId: string,
    transform: (item: WorkItem) => WorkItem,
): void {
    const updater = (
        old: WorkItemListResponse | undefined,
    ): WorkItemListResponse | undefined => {
        if (old === undefined) return undefined;
        const index = old.items.findIndex((i) => i.id === workItemId);
        if (index === -1) return old;
        const items = old.items.map((item, i) => (i === index ? transform(item) : item));
        return { ...old, items };
    };
    queryClient.setQueriesData({ queryKey: QUEUE_LIST_KEY_PREFIX }, updater);
    queryClient.setQueriesData({ queryKey: MINE_LIST_KEY_PREFIX }, updater);
}

export function patchWorkItemLockedInWorkflowDetail(
    queryClient: QueryClient,
    notification: WorkItemLockedNotification,
): void {
    const patch = lockPatchFrom(notification);
    patchItemInWorkflowDetail(queryClient, notification.workflowId, notification.workItemId, (item) =>
        applyLockToItem(item, patch),
    );
}

export function patchWorkItemUnlockedInWorkflowDetail(
    queryClient: QueryClient,
    notification: WorkItemUnlockedNotification,
): void {
    patchItemInWorkflowDetail(
        queryClient,
        notification.workflowId,
        notification.workItemId,
        applyUnlockToItem,
    );
}

export function patchWorkItemLockedInQueueLists(
    queryClient: QueryClient,
    notification: WorkItemLockedNotification,
): void {
    const patch = lockPatchFrom(notification);
    patchItemInQueueLists(queryClient, notification.workItemId, (item) =>
        applyLockToItem(item, patch),
    );
}

export function patchWorkItemUnlockedInQueueLists(
    queryClient: QueryClient,
    notification: WorkItemUnlockedNotification,
): void {
    patchItemInQueueLists(queryClient, notification.workItemId, applyUnlockToItem);
}

// ---------------------------------------------------------------------------
// Edit patch (note edit). The event carries the post-edit body — the only
// field a body edit changes — so the row is patched in place rather than
// refetched, like completion/snooze/lock. Never adds or removes rows.
// There is no per-item edit timestamp to guard against a stale racing
// publish. A wrong body stays until the detail refetches: on a later add
// event, after a hub gap (`realtimeLifecycle.ts`), or on remount once
// stale. No timer refetches it while the hub is up. A missing/null body
// on the wire is ignored rather than blanking a note.
// ---------------------------------------------------------------------------

function applyEditToItem(
    item: WorkItem,
    notification: WorkItemEditedNotification,
): WorkItem {
    if (notification.body === null || notification.body === undefined) {
        return item;
    }
    if (item.body === notification.body) {
        return item;
    }
    return { ...item, body: notification.body };
}

export function patchWorkItemEditedInWorkflowDetail(
    queryClient: QueryClient,
    notification: WorkItemEditedNotification,
): void {
    patchItemInWorkflowDetail(
        queryClient,
        notification.workflowId,
        notification.workItemId,
        (item) => applyEditToItem(item, notification),
    );
}
